import { useEffect } from 'react';
import { CircularProgress, CssBaseline } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community';

import { LoginPage } from 'components/login/LoginPage';
import { RegisterPage } from 'components/login/RegisterPage';
import { TermsPage } from 'components/login/TermsPage';
import { ResetPasswordEmailPage } from 'components/login/ResetPasswordEmailPage';
import { ConfirmEmailPage } from 'components/login/ConfirmEmailPage';
import { ResetPasswordPage } from 'components/login/ResetPasswordPage';
import { AdminPage } from 'components/admin/AdminPage';
import { ContactPage } from 'components/contact/ContactPage';
import { DiligencePage } from 'components/diligence/Page';
import { RequestAccessPage } from 'components/diligence/RequestAccessPage';
import { SettingsPage } from 'components/settings/SettingsPage';
import { CompleteProfilePage } from 'components/settings/CompleteProfilePage';

import { useRootStore } from 'stores/RootStore';
import { JobsViewPage } from './jobs/JobsViewPage';

import AmplitudeService from 'services/amplitude.service';
import { UnderwritingPage } from './underwriting/UnderwritingPage';
import CompDatabasePage from './comp-database';
import { UnderwritingDashboardPage } from './underwriting/dashboard/UnderwritingDashboardPage';
import { PropertyUnderwritingPages, UnderwritingRouteName } from 'stores/RouteStore';
import { featureFlags } from 'utils/config';

ModuleRegistry.registerModules([AllCommunityModule]);

const App = observer(() => {
  const { routeStore, authStore } = useRootStore();
  const { getFeatureFlagValueForUser } = authStore;
  const { route } = routeStore;

  useEffect(() => {
    AmplitudeService.initialize();
  }, []);

  useEffect(() => {
    AmplitudeService.setUser(authStore?.currentUser);
  }, [authStore?.currentUser]);

  // Temporary flag to show the new underwriting tech and hide pages if not in it
  // Eventually this will be handled by user permissioning
  const inUnderwritingBeta = getFeatureFlagValueForUser(featureFlags.showNewClientTech);

  useEffect(() => {
    if (!route) return;
    if (route === 'underwritingDashboard') {
      AmplitudeService.trackEvent('View Underwriting Dashboard', { user: authStore?.currentUser });
    } else if (route === 'compDatabase') {
      AmplitudeService.trackEvent('View Comp Database', { user: authStore?.currentUser });
    } else if (PropertyUnderwritingPages.includes(route as UnderwritingRouteName)) {
      // Try to get property id from URL
      const match = window.location.pathname.match(/property\/(.*?)\//);
      const propertyId = match ? match[1] : undefined;
      AmplitudeService.trackEvent('View Underwriting Page', { route, propertyId });
    }
  }, [authStore?.currentUser, route]);

  if (!route) return null;

  if (authStore?.currentUser && !authStore.featureFlags) {
    return <CircularProgress />;
  }

  return (
    <>
      <CssBaseline />
      {route === 'register' ? (
        <RegisterPage />
      ) : route === 'login' ? (
        <LoginPage />
      ) : ['loginContact', 'registerContact'].includes(route) ? (
        <ContactPage />
      ) : route === 'jobs' ? (
        <JobsViewPage />
      ) : route === 'underwritingDashboard' && inUnderwritingBeta ? (
        <UnderwritingDashboardPage />
      ) : route === 'compDatabase' && inUnderwritingBeta ? (
        <CompDatabasePage />
      ) : PropertyUnderwritingPages.includes(route as UnderwritingRouteName) && inUnderwritingBeta ? (
        <UnderwritingPage activePage={route as UnderwritingRouteName} />
      ) : ['loginTerms', 'registerTerms'].includes(route) ? (
        <TermsPage />
      ) : route === 'resetPasswordEmail' ? (
        <ResetPasswordEmailPage />
      ) : route === 'resetPassword' ? (
        <ResetPasswordPage />
      ) : ['confirmEmailSent', 'confirmEmailCheckToken'].includes(route) ? (
        <ConfirmEmailPage />
      ) : route === 'completeProfile' ? (
        <CompleteProfilePage />
      ) : ['job', 'diligence'].includes(route) ? (
        <DiligencePage />
      ) : ['requestAccess'].includes(route) ? (
        <RequestAccessPage />
      ) : ['createUser', 'editUsers', 'editUser'].includes(route) ? (
        <AdminPage />
      ) : ['account', 'userProfile', 'confirmEmailSent', 'confirmEmailCheckToken', 'notifications'].includes(route) ? (
        <SettingsPage />
      ) : //add 404
      null}
    </>
  );
});

export { App };
